import { format } from 'date-fns'
import { Check, Pencil } from 'lucide-react'

import { LeadDistributionPreference } from 'src/shared/features/enterprise/api/use-update-enterprise-settings'
import type { EnterpriseCompanyTypeValues } from 'src/shared/schemas/forms/auth'
import { cn } from 'src/shared/utils/cn'

import type { EnterpriseDistributionSystemResponse } from '../api/use-lead-distributions'

import { getMemberPluralLabel } from './lead-distribution-member-picker'

function distributionTypeLabel(type: LeadDistributionPreference): string {
  if (type === LeadDistributionPreference.RANDOM) return 'Random'
  return 'Round robin'
}

function formatCreatedOn(iso: string): string {
  try {
    return format(new Date(iso), 'MM/dd/yy')
  } catch {
    return ''
  }
}

type LeadDistributionListProps = {
  distributions: EnterpriseDistributionSystemResponse[]
  totalMembers: number | null | undefined
  companyType: EnterpriseCompanyTypeValues | undefined
  onEdit: (distribution: EnterpriseDistributionSystemResponse) => void
  /** Called when a distribution is clicked */
  onClick?: (distribution: EnterpriseDistributionSystemResponse) => void
  /** Distribution IDs to highlight */
  highlightDistributionIds?: number[]
  /**
   * 'default' - Default list with edit button
   * 'with-checkboxes' - List with checkmarks, can be checked with highlightDistributionIds.
   */
  variant?: 'default' | 'with-checkboxes'
}

export default function LeadDistributionList({
  distributions,
  totalMembers,
  companyType,
  onEdit,
  onClick,
  highlightDistributionIds,
  variant = 'default',
}: LeadDistributionListProps) {
  const plural = getMemberPluralLabel(companyType)

  return (
    <div className="flex flex-col gap-2.5">
      {distributions.map((dist) => {
        const memberCount = dist.memberUserIds.length
        const everyone = totalMembers != null && totalMembers > 0 && memberCount >= totalMembers ? true : false
        const scopeLabel = everyone ? 'Everyone' : `${memberCount} ${plural}`
        const isHighlighted = highlightDistributionIds?.includes(dist.id)

        return (
          <div
            key={dist.id}
            className={cn(
              'flex flex-col gap-4 rounded-2xl bg-accent px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6',
              onClick && 'cursor-pointer',
              isHighlighted && 'bg-border',
            )}
            onClick={() => onClick?.(dist)}
          >
            {variant === 'with-checkboxes' && (
              <span
                className={cn(
                  '-mr-2 flex size-[25px] shrink-0 items-center justify-center rounded-full bg-white text-border/100 transition-colors',
                  isHighlighted && 'bg-primary text-white',
                )}
              >
                <Check size={14} strokeWidth={2} />
              </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-[15px] font-medium leading-snug tracking-[-0.15px] text-primary">{dist.name}</p>
              <div className="flex flex-wrap items-center gap-1 text-[13px] font-medium text-muted">
                <span>{scopeLabel}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1.5">{distributionTypeLabel(dist.distributionType)}</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-row items-center justify-between gap-4 sm:justify-end">
              <p className="text-xs font-medium text-muted sm:text-right">
                Created on {formatCreatedOn(dist.createdAt)}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(dist)
                }}
                className={cn(
                  'inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-lightBorder bg-white px-4 text-sm font-medium tracking-[-0.14px] text-primary',
                  'transition-opacity hover:opacity-80',
                )}
              >
                <Pencil size={16} strokeWidth={2} className="text-primary" />
                Edit
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
